import React, { useMemo, useState } from "react";
import { useTable } from "@/lib/store";
import { api, errMsg } from "@/lib/api";
import { toast } from "sonner";
import { Card, PageHead, Kicker, Segmented, Bar, Sparkline } from "@/components/kit";
import { CHECKPOINTS } from "@/lib/util";

export default function Baseline() {
  const { me, settings, baseline, yearId, refresh, canWrite, activeMembers } = useTable();
  const dims = settings?.dimensions || [];
  const [checkpoint, setCheckpoint] = useState("Baseline");

  const mine = baseline.find((b) => b.memberId === me.memberId && b.checkpoint === checkpoint);
  const [draft, setDraft] = useState(null);
  const scores = draft ?? mine?.scores ?? {};

  const setDim = (i, v) => setDraft({ ...scores, [`d${i + 1}`]: v });

  const save = async () => {
    try {
      await api.post("/baseline", { yearId, checkpoint, scores });
      setDraft(null);
      await refresh();
      toast.success("Recorded.");
    } catch (e) { toast.error(errMsg(e)); }
  };

  const trend = CHECKPOINTS.map((cp) => {
    const row = baseline.find((b) => b.memberId === me.memberId && b.checkpoint === cp);
    if (!row) return null;
    const vals = dims.map((_, i) => row.scores?.[`d${i + 1}`]).filter(Boolean);
    return vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : null;
  }).filter((v) => v !== null);

  const pulse = useMemo(
    () =>
      dims.map((_, i) => {
        const vals = baseline
          .filter((b) => b.checkpoint === checkpoint)
          .map((b) => b.scores?.[`d${i + 1}`])
          .filter(Boolean);
        return vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : null;
      }),
    [baseline, checkpoint, dims],
  );

  return (
    <div data-testid="baseline-page">
      <PageHead
        kicker="The instrument"
        title="Baseline"
        sub="The Sanity North Star instrument. Each dimension scored one to ten, at every checkpoint."
      />

      <div className="mb-6">
        <Kicker className="mb-2">Checkpoint</Kicker>
        <Segmented options={CHECKPOINTS} value={checkpoint} onChange={(v) => { setCheckpoint(v); setDraft(null); }} testId="checkpoint" />
      </div>

      <Card data-testid="baseline-form">
        <Kicker>Your scores · {checkpoint}</Kicker>
        <div className="mt-5 space-y-5">
          {dims.map((d, i) => (
            <div key={d}>
              <div className="flex justify-between items-baseline mb-2">
                <span className="serif text-lg">{d}</span>
                <span className="serif text-lg" style={{ color: "var(--brass)" }}>{scores[`d${i + 1}`] || "—"}</span>
              </div>
              <Segmented
                options={[1, 2, 3, 4, 5, 6, 7, 8, 9, 10]}
                value={scores[`d${i + 1}`]}
                onChange={(v) => setDim(i, Number(v))}
                testId={`dim-${i + 1}`}
              />
            </div>
          ))}
        </div>
        {canWrite && (
          <button className="btn-ink mt-6 w-full sm:w-auto" data-testid="baseline-save" onClick={save}>
            Save checkpoint
          </button>
        )}
      </Card>

      <div className="grid md:grid-cols-2 gap-4 mt-6">
        <Card>
          <Kicker>Your trend</Kicker>
          <div className="mt-4"><Sparkline points={trend} width={240} height={48} /></div>
        </Card>
        <Card data-testid="forum-pulse">
          <Kicker>Forum pulse · {checkpoint}</Kicker>
          <p className="text-[11px] mt-1 italic" style={{ color: "var(--ink-mute)" }}>no rankings, no names</p>
          <div className="mt-4 space-y-3">
            {dims.map((d, i) => (
              <div key={d}>
                <div className="flex justify-between text-xs mb-1.5">
                  <span>{d}</span>
                  <span style={{ color: "var(--brass)" }}>{pulse[i] === null ? "—" : pulse[i].toFixed(1)}</span>
                </div>
                <Bar value={(pulse[i] || 0) * 10} />
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
}
